// First, before any module builds a Zod schema.
import '@/shared/config/zod-config'

import type { IPublicClientApplication } from '@azure/msal-browser'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import { StrictMode, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { useIntl } from 'react-intl'

import App from '@/App'
import { takeSignInAnswer } from '@/features/auth/sign-in-answer'
import { createMsalInstance, forgetFailedHopTab, isHopTab } from '@/shared/auth/msal-instance'
import { isSignedInThroughHop, takeHopResult, takeOwnHopResult, type HopResult } from '@/shared/auth/sign-in-hop'
import { dropRetiredStorage } from '@/shared/auth/signed-in-storage'
import { loadRuntimeConfig, type RuntimeConfig } from '@/shared/config/runtime-config'
import { I18nProvider, loadStartingCatalog, type LoadedCatalog } from '@/shared/i18n/I18nProvider'
import { readBrowserLocale, type BrowserLocale } from '@/shared/i18n/locale-settings'
import { configureLogging } from '@/shared/logging'
import { AppThemeProvider } from '@/shared/theme/AppThemeProvider'

// Inter and Lora from the UI's own origin, in the weights the theme uses; index.css has the icons.
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/lora/500.css'
import '@fontsource/lora/600.css'
import './index.css'

const container = document.getElementById('root')
if (container === null) {
  throw new Error('The #root element is missing from index.html.')
}

// ErrorBoundary logs what it catches; React's default would log it a second time.
const root = createRoot(container, { onCaughtError: () => undefined })

/** Hoisted so the object identity is stable across renders. */
const errorDetailSx: SxProps<Theme> = { mt: 2, whiteSpace: 'pre-wrap' }

/**
 * Shown when `/config.json` cannot be loaded or used.
 *
 * @param props - The failure message to surface.
 * @returns The error page element.
 */
function ConfigurationErrorPage(props: { readonly message: string }) {
  const intl = useIntl()

  return (
    <main className="mx-auto max-w-shell px-6 py-12">
      <Alert severity="error">
        <AlertTitle>{intl.formatMessage({ id: 'shell.startup.configError.title' })}</AlertTitle>
        <Typography variant="body2">
          {intl.formatMessage({ id: 'shell.startup.configError.body' })}
        </Typography>
        <Typography variant="caption" component="pre" sx={errorDetailSx}>
          {props.message}
        </Typography>
      </Alert>
    </main>
  )
}

/**
 * Builds the MSAL client without consuming the URL; `null` when auth is disabled, or on an address
 * that signs in through the sign-in site, where B2C never returns. A tab passing another site's hop
 * through keeps that sign-in in its own cache, never the one this site's tabs share.
 */
async function createAuthenticationClient(
  config: RuntimeConfig,
): Promise<IPublicClientApplication | null> {
  if (config.auth.mode === 'disabled' || isSignedInThroughHop(config.auth)) {
    return null
  }

  return await createMsalInstance(config.auth, window.location.origin, isHopTab(config.auth.redirectPath))
}

function renderApplication(
  element: ReactNode,
  browser: BrowserLocale,
  catalog: LoadedCatalog,
): void {
  root.render(
    <StrictMode>
      <AppThemeProvider>
        <I18nProvider browser={browser} initial={catalog}>
          {element}
        </I18nProvider>
      </AppThemeProvider>
    </StrictMode>,
  )
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * Resolves configuration and authentication before the first render. A configuration failure means
 * a broken deployment; a sign-in failure happens in ordinary use, and `takeSignInAnswer` settles it.
 */
async function bootstrap(): Promise<void> {
  dropRetiredStorage()
  const browser = readBrowserLocale()
  // Fetched alongside the configuration. Every page needs it, the error pages too.
  const catalog = loadStartingCatalog(browser)
  const render = async (element: ReactNode) => {
    renderApplication(element, browser, await catalog)
  }

  let config: RuntimeConfig
  let msalInstance: IPublicClientApplication | null
  let hopResult: HopResult | null = null

  try {
    config = await loadRuntimeConfig()
    // Out of the address before logging starts, so no log entry's address ever carries it. On the
    // sign-in site, only its own hop's, as a sign-in link for it makes (SM-39), before MSAL reads it.
    hopResult = isSignedInThroughHop(config.auth)
      ? takeHopResult(config.auth.redirectPath)
      : config.auth.mode === 'b2c' ? takeOwnHopResult(config.auth.redirectPath) : null
    configureLogging(config)
    msalInstance = await createAuthenticationClient(config)
  } catch (error: unknown) {
    forgetFailedHopTab()
    await render(<ConfigurationErrorPage message={describe(error)} />)
    return
  }

  const answer = await takeSignInAnswer(config, msalInstance, hopResult)
  if (answer === null) {
    return
  }

  await render(<App config={config} msalInstance={msalInstance} signInFailure={answer.failure} />)
}

void bootstrap()
